import { subscribeUnityRuntimeBridgeState, getUnityRuntimeBridgeSnapshot } from "./unityRuntimeBridgeState";

export const LOADER_CLEANUP_STATUS_EVENT = "unity:loaderAttemptCleanupComplete";
export type LoaderCleanupStatus = {
  status: "loader_attempt_cleanup_complete";
  loader_job_id: string;
  generation: number;
  success: boolean;
  ownerId?: string;
  executionId?: string;
};

// Register before sending: the synchronous WebGL response can arrive inside send().
export async function requestExactLoaderCleanup(
  loaderJobId: string,
  generation: number,
  send: () => Promise<void>,
  assertCurrent?: () => void,
  timeoutMs = 10_000,
): Promise<LoaderCleanupStatus> {
  let dispose = () => {};
  const completion = new Promise<LoaderCleanupStatus>((resolve, reject) => {
    let settled = false;
    const finish = (error?: Error, result?: LoaderCleanupStatus) => {
      if (settled) return;
      settled = true;
      dispose();
      if (error) reject(error); else resolve(result!);
    };
    const receive = (event: Event) => {
      const payload = (event as CustomEvent<LoaderCleanupStatus>).detail;
      if (payload?.loader_job_id !== loaderJobId || payload?.generation !== generation) return;
      try { assertCurrent?.(); } catch { finish(new Error("loader_cleanup_execution_stale")); return; }
      finish(payload.success === true ? undefined : new Error("loader_cleanup_failed"), payload);
    };
    window.addEventListener(LOADER_CLEANUP_STATUS_EVENT, receive);
    const unsubscribe = subscribeUnityRuntimeBridgeState(() => {
      if (getUnityRuntimeBridgeSnapshot().readyState === "booting") finish(new Error("loader_cleanup_runtime_replaced"));
    });
    const timeout = window.setTimeout(() => finish(new Error("loader_cleanup_timeout")), timeoutMs);
    dispose = () => {
      window.removeEventListener(LOADER_CLEANUP_STATUS_EVENT, receive);
      window.clearTimeout(timeout);
      unsubscribe();
    };
  });
  try {
    // Attach a rejection handler to the waiter before send can synchronously fail.
    const [result] = await Promise.all([completion, Promise.resolve().then(() => { assertCurrent?.(); return send(); })]);
    assertCurrent?.();
    return result;
  } finally { dispose(); }
}
